//
/* GS://PROTOCOLO
 * Arquivo: src/pages/lots/LotsListPage.jsx
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 1.4.0 – CLEANUP: Interface Limpa e Botões Inteligentes
 * Impacto da Versão: [src/pages/lots/LotsListPage.jsx]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [UX]: Layout da tabela expandido (mais espaçamento e respiro).
 * - [LÓGICA]: Botão "Banho" só aparece se houver peças BRUTAS no lote.
 * - [VISUAL]: Lotes 100% Serviço aparecem com status visual "REGISTRADO" (Cinza) em vez de "ABERTO".
 * - [VISUAL]: Lotes 100% Pronta Entrega sem pendência de banho não mostram botão de ação desnecessário.
 */
//

import React, { useEffect, useState } from 'react';
import { getLots } from '../../services/lotsService';
import { Loader2, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

const LotsListPage = () => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterTerm, setFilterTerm] = useState(''); // Busca geral (NF, Fornecedor, Código)
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getLots();
        setLots(data);
      } catch (error) {
        console.error('[LotsListPage] Erro ao buscar lotes:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const applyFilters = (lot) => {
    // Normalização para busca
    const term = filterTerm.toLowerCase();
    const searchMatch = 
      !term || 
      lot.nfNumber?.toLowerCase().includes(term) ||
      lot.supplierName?.toLowerCase().includes(term) ||
      lot.lotCode?.toLowerCase().includes(term);

    if (!searchMatch) return false;

    // Filtro de Data (ISO yyyy-MM-dd)
    if (filterDate && lot.nfDateISO !== filterDate) {
      return false;
    }

    if (filterStatus && lot.statusLot !== filterStatus) {
      return false;
    }

    return true;
  };

  const filteredLots = lots.filter(applyFilters);

  // Função auxiliar para determinar o status visual e ações
  const getLotState = (lot) => {
    const qtdBrutas = lot.counts?.qtdBrutas || lot.totalRawItems || 0;
    const qtdProntas = lot.counts?.qtdProntas || lot.totalFinishedItems || 0;
    const qtdServicos = lot.counts?.qtdServicos || lot.totalServiceItems || 0;
    const totalItens = qtdBrutas + qtdProntas + qtdServicos;

    const isServiceOnly = qtdServicos > 0 && qtdBrutas === 0 && qtdProntas === 0;
    const isReadyOnly = qtdProntas > 0 && qtdBrutas === 0;
    
    // Define se botão de Banho deve aparecer
    const showBathButton = qtdBrutas > 0;

    // Define label de Status Visual
    let statusLabel = lot.statusLot || 'ABERTO';
    let statusColor = 'text-yellow-500'; // Padrão Aberto

    if (statusLabel === 'FINALIZADO') {
      statusColor = 'text-green-500';
    } else if (isServiceOnly) {
      statusLabel = 'SERVIÇO'; // Visual apenas
      statusColor = 'text-gray-400';
    } else if (isReadyOnly && statusLabel === 'ABERTO') {
      statusLabel = 'A INTEGRAR'; // Peça pronta aguardando ir pro estoque
      statusColor = 'text-blue-400';
    }

    return { showBathButton, statusLabel, statusColor, totalItens, isServiceOnly };
  };

  return (
    <div className="p-6 text-white bg-brand-black min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestão de Lotes</h1>
          <p className="text-gray-400 text-sm mt-1">Acompanhe a evolução de NFs, Banhos e Integrações.</p>
        </div>
      </div>

      {/* Barra de Filtros Unificada */}
      <div className="bg-brand-dark p-4 rounded-lg border border-gray-700 mb-6 shadow-lg">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          
          {/* Busca Inteligente */}
          <div className="flex-1 w-full">
            <label className="text-xs text-gray-500 font-semibold uppercase mb-1 block">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-500 h-4 w-4" />
              <input
                type="text"
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded border border-gray-600 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold text-sm"
                placeholder="Código, NF ou Fornecedor..."
              />
            </div>
          </div>

          {/* Data */}
          <div className="w-full md:w-48">
            <label className="text-xs text-gray-500 font-semibold uppercase mb-1 block">Data NF</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600 focus:border-brand-gold text-sm"
            />
          </div>

          {/* Status */}
          <div className="w-full md:w-48">
            <label className="text-xs text-gray-500 font-semibold uppercase mb-1 block">Status Real</label>
            <div className="relative">
              <Filter className="absolute left-3 top-2.5 text-gray-500 h-4 w-4" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded border border-gray-600 focus:border-brand-gold text-sm appearance-none"
              >
                <option value="">Todos</option>
                <option value="ABERTO">Aberto</option>
                <option value="FINALIZADO">Finalizado</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-brand-dark rounded-lg border border-gray-700 overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-brand-gold" size={40} />
          </div>
        ) : filteredLots.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg">Nenhum lote encontrado com estes filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-800 text-gray-400 text-xs uppercase tracking-wider border-b border-gray-700">
                  <th className="px-6 py-4 font-semibold">Código Lote</th>
                  <th className="px-6 py-4 font-semibold">NF Origem</th>
                  <th className="px-6 py-4 font-semibold">Data</th>
                  <th className="px-6 py-4 font-semibold">Fornecedor</th>
                  <th className="px-6 py-4 font-semibold text-center">Itens</th>
                  <th className="px-6 py-4 font-semibold text-center">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredLots.map((lot) => {
                  const { showBathButton, statusLabel, statusColor, totalItens } = getLotState(lot);

                  return (
                    <tr key={lot.id} className="hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-brand-gold text-sm whitespace-nowrap">
                        {lot.lotCode}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-white">
                        {lot.nfNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                        {lot.nfDateDisplay || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {lot.supplierName || 'Fornecedor N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <span className="bg-gray-700 text-gray-200 px-2 py-1 rounded text-xs font-bold">
                          {totalItens}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full border border-current opacity-80 ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap space-x-3">
                        {/* Ação Principal: Ver Detalhes */}
                        <Link
                          to={`/lotes/${lot.id}`}
                          className="text-sm text-gray-300 hover:text-white font-medium underline decoration-gray-600 underline-offset-4 hover:decoration-white"
                        >
                          Ver Detalhes
                        </Link>

                        {/* Ação Secundária: Banho (Condicional) */}
                        {showBathButton && (
                          <Link
                            to={`/lotes/${lot.id}/banho`}
                            className="inline-flex items-center justify-center px-4 py-1.5 rounded bg-brand-gold text-brand-black text-xs font-bold hover:bg-yellow-500 shadow-sm transition-transform active:scale-95"
                          >
                            Banho
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LotsListPage;